import { useState, useEffect, useCallback } from 'react';
import {
  Brain, Loader2, CheckCircle2, XCircle, RefreshCw, Activity, Layers, Database,
  TrendingDown, Clock, ChevronDown, ChevronUp, ListTodo, FileCheck, FlaskConical,
  ArrowDown, ArrowUp, Minus,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts';
import { workshopApi } from '../../../services/api';
import type {
  StyleRule, StyleRuleState, TemplateProposal, LearningMetrics, BanditMetrics,
} from '../../../types';

const STATE_LABEL: Record<StyleRuleState, string> = {
  candidate: 'Candidate', active: 'Active', retracted: 'Retracted',
};
const STATE_COLOR: Record<StyleRuleState, string> = {
  candidate: 'bg-gray-100 dark:bg-surface-850 text-gray-700 dark:text-gray-300',
  active:    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  retracted: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
};

const PROPOSAL_STATUS_COLOR: Record<string, string> = {
  pending:    'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
  approved:   'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  rejected:   'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  superseded: 'bg-gray-100 dark:bg-surface-850 text-gray-500 dark:text-gray-400',
};

export default function LearningView() {
  const [metrics, setMetrics] = useState<LearningMetrics | null>(null);
  const [rules, setRules] = useState<StyleRule[]>([]);
  const [proposals, setProposals] = useState<TemplateProposal[]>([]);
  const [bandit, setBandit] = useState<BanditMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<StyleRuleState | ''>('');
  const [proposalStatusFilter, setProposalStatusFilter] = useState<string>('pending');
  const [expandedProposalId, setExpandedProposalId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, r, p, b] = await Promise.all([
        workshopApi.learningMetrics(60),
        workshopApi.listStyleRules({ state: stateFilter || undefined, limit: 300 }),
        workshopApi.listTemplateProposals({ status: (proposalStatusFilter as TemplateProposal['status']) || undefined }),
        workshopApi.banditMetrics().catch(() => null),
      ]);
      setMetrics(m);
      setRules(r);
      setProposals(p);
      setBandit(b);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load learning data');
    } finally {
      setLoading(false);
    }
  }, [stateFilter, proposalStatusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleApprove = async (p: TemplateProposal) => {
    const note = prompt('Approval note (optional):') ?? undefined;
    await workshopApi.approveTemplateProposal(p.id, note);
    fetchAll();
  };
  const handleReject = async (p: TemplateProposal) => {
    const note = prompt('Reason for rejection (required-ish — helps the next iteration):') ?? undefined;
    if (note === null) return;
    await workshopApi.rejectTemplateProposal(p.id, note);
    fetchAll();
  };

  if (loading && !metrics) {
    return <div className="p-4 text-sm text-gray-500"><Loader2 size={14} className="animate-spin inline mr-1" />Loading learning data…</div>;
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header with refresh */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-primary-600 dark:text-primary-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Learning engine</h3>
          {metrics?.last_bootstrap_at && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
              <Clock size={10} /> last bootstrap {new Date(metrics.last_bootstrap_at).toLocaleString()}
            </span>
          )}
        </div>
        <button onClick={fetchAll} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-surface-850 hover:bg-gray-200 dark:hover:bg-surface-800 rounded text-gray-700 dark:text-gray-300">
          <RefreshCw size={12} />Refresh
        </button>
      </div>

      {error && (
        <div className="p-2 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Layered architecture metrics */}
      {metrics && (
        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <LayerCard
            icon={Activity}
            title="Online layer"
            subtitle="edits & substitution detection"
            color="text-blue-600 dark:text-blue-400"
            primary={`${metrics.edit_daily.reduce((s, d) => s + d.edits, 0)}`}
            primaryLabel="reports edited (60d)"
            secondary={metrics.edit_daily.length
              ? `avg distance: ${Math.round(metrics.edit_daily.reduce((s, d) => s + d.avg_distance, 0) / Math.max(1, metrics.edit_daily.filter(d => d.avg_distance > 0).length))} chars`
              : 'no recent edits'}
          />
          <LayerCard
            icon={Layers}
            title="Hourly layer"
            subtitle="rule promotion & retraction"
            color="text-amber-600 dark:text-amber-400"
            primary={`${metrics.rule_states.active ?? 0}`}
            primaryLabel="active rules"
            secondary={`${metrics.rule_states.candidate ?? 0} candidates · ${metrics.rule_states.retracted ?? 0} retracted`}
          />
          <LayerCard
            icon={FileCheck}
            title="Nightly layer"
            subtitle="template proposals"
            color="text-purple-600 dark:text-purple-400"
            primary={`${metrics.proposal_states.pending ?? 0}`}
            primaryLabel="pending review"
            secondary={`${metrics.proposal_states.approved ?? 0} approved · ${metrics.proposal_states.rejected ?? 0} rejected`}
          />
          <LayerCard
            icon={Database}
            title="Vocabulary profiles"
            subtitle="per radiologist × category"
            color="text-teal-600 dark:text-teal-400"
            primary={`${metrics.vocab_profile_count}`}
            primaryLabel="profiles stored"
            secondary={metrics.last_bootstrap_at ? `last refresh ${new Date(metrics.last_bootstrap_at).toLocaleDateString()}` : 'never refreshed'}
          />
        </div>
      )}

      {/* Edit distance chart — the key business metric */}
      {metrics && metrics.edit_daily.length > 0 && (
        <div className="mb-4 p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown size={14} className="text-primary-600 dark:text-primary-400" />
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Edit distance over time (lower = system learning)
            </span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={metrics.edit_daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip
                labelFormatter={(d) => new Date(d).toLocaleDateString()}
                contentStyle={{ fontSize: 12, background: 'rgba(255,255,255,0.95)' }}
              />
              <Line type="monotone" dataKey="avg_distance" stroke="#3b82f6" strokeWidth={2} dot={false} name="Avg edit distance" />
              <Line type="monotone" dataKey="edits" stroke="#94a3b8" strokeWidth={1} dot={false} name="Edit count" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Rule states bar chart */}
      {metrics && (metrics.rule_states.active || metrics.rule_states.candidate || metrics.rule_states.retracted) ? (
        <div className="mb-4 p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-amber-600 dark:text-amber-400" />
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Style rules by state
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={[
              { state: 'Candidate', count: metrics.rule_states.candidate ?? 0, fill: '#94a3b8' },
              { state: 'Active', count: metrics.rule_states.active ?? 0, fill: '#10b981' },
              { state: 'Retracted', count: metrics.rule_states.retracted ?? 0, fill: '#ef4444' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
              <XAxis dataKey="state" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count">
                {[
                  { fill: '#94a3b8' },
                  { fill: '#10b981' },
                  { fill: '#ef4444' },
                ].map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* Template proposals queue */}
      <section className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <FileCheck size={14} />
            Template proposals
          </h4>
          <select
            value={proposalStatusFilter}
            onChange={(e) => setProposalStatusFilter(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>

        {proposals.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No proposals match the current filter.</p>
        ) : (
          <div className="space-y-2">
            {proposals.map((p) => {
              const expanded = expandedProposalId === p.id;
              return (
                <div key={p.id} className="border border-gray-200 dark:border-surface-800 rounded">
                  <button
                    onClick={() => setExpandedProposalId(expanded ? null : p.id)}
                    className="w-full p-2 text-left flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-surface-850"
                  >
                    {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{p.exam_category}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PROPOSAL_STATUS_COLOR[p.status] ?? ''}`}>
                      {p.status}
                    </span>
                    <span className="text-[10px] text-gray-500 ml-auto">
                      derived from {p.derived_from_count} reports · {new Date(p.derived_at).toLocaleDateString()}
                    </span>
                  </button>

                  {expanded && (
                    <div className="p-3 border-t border-gray-200 dark:border-surface-800 bg-gray-50 dark:bg-surface-850 space-y-2">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        For: <span className="font-mono">{p.user_email ?? p.user_id}</span> · model: <span className="font-mono">{p.derived_by}</span>
                      </div>
                      {p.body?.notes && (
                        <div className="text-xs italic text-gray-700 dark:text-gray-300">
                          <span className="font-semibold not-italic">LLM note:</span> {p.body.notes}
                        </div>
                      )}

                      <div>
                        <div className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">Proposed sections</div>
                        <div className="space-y-1.5">
                          {(p.body?.sections ?? []).map((s, i) => (
                            <div key={i} className="text-xs p-2 bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-800 rounded">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 dark:text-white">{s.name}</span>
                                {s.conditional && <span className="text-[9px] px-1 rounded bg-amber-100 dark:bg-amber-900/20 text-amber-700">conditional</span>}
                                <span className="ml-auto font-mono text-[10px] text-gray-500">slot: {s.slot_name}</span>
                              </div>
                              {s.fixed_prefix && (
                                <div className="mt-1 text-gray-700 dark:text-gray-300">
                                  <span className="text-[10px] font-semibold text-gray-500">Fixed prefix:</span>
                                  <pre className="whitespace-pre-wrap text-xs font-sans mt-0.5">{s.fixed_prefix}</pre>
                                </div>
                              )}
                              {s.normal_default && (
                                <div className="mt-1 text-gray-700 dark:text-gray-300">
                                  <span className="text-[10px] font-semibold text-gray-500">Normal default:</span>
                                  <pre className="whitespace-pre-wrap text-xs font-sans mt-0.5">{s.normal_default}</pre>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {p.review_notes && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-semibold">Review notes:</span> {p.review_notes}
                        </div>
                      )}

                      {p.status === 'pending' && (
                        <div className="flex items-center gap-2 justify-end pt-2 border-t border-gray-200 dark:border-surface-800">
                          <button onClick={() => handleReject(p)} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 rounded font-medium">
                            <XCircle size={12} />Reject
                          </button>
                          <button onClick={() => handleApprove(p)} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded font-medium">
                            <CheckCircle2 size={12} />Approve
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Bandit metrics — Phase 6 */}
      {bandit && (
        <section className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
              <FlaskConical size={14} />
              Bandit metrics
              <span className="text-xs font-normal text-gray-500">
                ({bandit.candidates.length} candidate{bandit.candidates.length === 1 ? '' : 's'} on trial)
              </span>
            </h4>
            <span className="text-[10px] text-gray-500">
              decision: need ≥{bandit.decision_thresholds.min_trials_per_arm}/arm and Δ≥{bandit.decision_thresholds.min_effect_margin} chars
            </span>
          </div>

          {bandit.candidates.length === 0 ? (
            <p className="text-xs text-gray-500 italic">
              No candidate rules currently. Bandit kicks in once online learning produces candidates.
            </p>
          ) : (
            <div className="overflow-x-auto border border-gray-200 dark:border-surface-800 rounded">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-surface-850 text-left">
                  <tr>
                    <th className="px-2 py-1.5 font-medium text-gray-500">Rule</th>
                    <th className="px-2 py-1.5 font-medium text-gray-500 text-right">Applied n / mean</th>
                    <th className="px-2 py-1.5 font-medium text-gray-500 text-right">Control n / mean</th>
                    <th className="px-2 py-1.5 font-medium text-gray-500 text-right">Effect</th>
                    <th className="px-2 py-1.5 font-medium text-gray-500 text-right">Days</th>
                    <th className="px-2 py-1.5 font-medium text-gray-500">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {bandit.candidates.map((c) => {
                    const minN = Math.min(c.applied.n, c.not_applied.n);
                    const ready = minN >= bandit.decision_thresholds.min_trials_per_arm;
                    const margin = bandit.decision_thresholds.min_effect_margin;
                    let verdict: { label: string; icon: typeof ArrowDown; color: string } = {
                      label: 'gathering',
                      icon: Minus,
                      color: 'text-gray-500',
                    };
                    if (ready && c.effect_size !== null) {
                      if (c.effect_size <= -margin) verdict = { label: 'will promote', icon: ArrowDown, color: 'text-green-600' };
                      else if (c.effect_size >= margin) verdict = { label: 'will retract', icon: ArrowUp, color: 'text-red-600' };
                      else verdict = { label: 'inconclusive', icon: Minus, color: 'text-amber-600' };
                    } else if (!ready) {
                      verdict = { label: `need ${bandit.decision_thresholds.min_trials_per_arm - minN} more`, icon: Minus, color: 'text-gray-500' };
                    }
                    const VIcon = verdict.icon;
                    return (
                      <tr key={c.rule_id} className="border-t border-gray-100 dark:border-surface-800">
                        <td className="px-2 py-1.5">
                          <span className="line-through text-gray-500">{c.trigger_phrase.slice(0, 40)}</span>
                          {' → '}
                          <span className="text-green-700 dark:text-green-400">{c.replacement_phrase ? c.replacement_phrase.slice(0, 40) : <em className="text-gray-500">&lt;delete&gt;</em>}</span>
                          <div className="text-[10px] text-gray-400">{c.exam_category}</div>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {c.applied.n}{c.applied.mean !== null ? ` / ${c.applied.mean}` : ''}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {c.not_applied.n}{c.not_applied.mean !== null ? ` / ${c.not_applied.mean}` : ''}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {c.effect_size === null ? '—' : (c.effect_size > 0 ? `+${c.effect_size}` : c.effect_size)}
                        </td>
                        <td className="px-2 py-1.5 text-right">{c.days_collected ?? '—'}</td>
                        <td className={`px-2 py-1.5 font-medium ${verdict.color}`}>
                          <VIcon size={10} className="inline mr-0.5" />{verdict.label}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Style rules list */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <ListTodo size={14} />
            Style rules
            <span className="text-xs font-normal text-gray-500">({rules.length})</span>
          </h4>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as StyleRuleState | '')}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          >
            <option value="">All states</option>
            <option value="active">Active</option>
            <option value="candidate">Candidate</option>
            <option value="retracted">Retracted</option>
          </select>
        </div>

        {rules.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 italic text-center border border-dashed border-gray-300 dark:border-surface-700 rounded">
            No rules yet. Run the bootstrap script against existing report_edits to seed.
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 dark:border-surface-800 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-surface-850 text-left">
                <tr>
                  <th className="px-2 py-1.5 font-medium text-gray-500 dark:text-gray-400">State</th>
                  <th className="px-2 py-1.5 font-medium text-gray-500 dark:text-gray-400">Category</th>
                  <th className="px-2 py-1.5 font-medium text-gray-500 dark:text-gray-400">Trigger → Replacement</th>
                  <th className="px-2 py-1.5 font-medium text-gray-500 dark:text-gray-400 text-right">Occurrences</th>
                  <th className="px-2 py-1.5 font-medium text-gray-500 dark:text-gray-400 text-right">Confidence</th>
                  <th className="px-2 py-1.5 font-medium text-gray-500 dark:text-gray-400">Source</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-surface-800">
                    <td className="px-2 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATE_COLOR[r.state]}`}>{STATE_LABEL[r.state]}</span></td>
                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{r.exam_category}</td>
                    <td className="px-2 py-1.5 text-gray-900 dark:text-white">
                      <span className="line-through text-gray-500">{r.trigger_phrase}</span>
                      {' → '}
                      <span className="text-green-700 dark:text-green-400">{r.replacement_phrase}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-700 dark:text-gray-300">{r.occurrences}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-700 dark:text-gray-300">{(r.confidence * 100).toFixed(0)}%</td>
                    <td className="px-2 py-1.5 text-[10px] text-gray-500 dark:text-gray-400">{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function LayerCard({
  icon: Icon, title, subtitle, color, primary, primaryLabel, secondary,
}: {
  icon: typeof Activity; title: string; subtitle: string; color: string;
  primary: string; primaryLabel: string; secondary: string;
}) {
  return (
    <div className="p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} className={color} />
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{title}</span>
      </div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">{subtitle}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{primary}</div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">{primaryLabel}</div>
      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1.5">{secondary}</div>
    </div>
  );
}
